import { useEffect, useState } from 'react';
import { Link } from '../routing';
import { analysisApi, formatCurrency } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import FilterSheet from './FilterSheet';
import { openScanInNewTab } from '../utils/openScan';

// What מספר מסמכים opens into — it used to be a dead-end count with no way to
// see which invoices it meant. `product` is the exact row the user clicked
// (carrying supplier_id + catalog_id, or sku/name when it has no catalog
// match), so the fetch below can only ever return the document set that
// produced that row's own qty/spend/doc_count — never an approximation of it.
//
// Two shells (owner, 23/10/2026, #318): a PHONE gets this FilterSheet — the
// bottom sheet for "the details behind one row"; from 641px the documents open
// as sub-lines under the product row instead (ProductDocumentsPanel, below),
// like רכש לפי ספקים and the top-products report. FilterSheet has no desktop
// layout: it stretched across the whole screen there.
export function useProductDocuments(product, params) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(false);
    analysisApi.getClassificationProductDocuments({
      date_from: params.date_from,
      date_to: params.date_to,
      supplier_id: product.supplier_id,
      ...(product.catalog_id != null
        ? { catalog_id: product.catalog_id }
        : { sku: product.sku, name: product.name }),
    })
      .then(r => { if (alive) setRows(r.data || []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
    // The fields it reads, not the row object: a search response rebuilds the
    // rows, and an open panel must not refetch for the same product (review #318).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.supplier_id, product.catalog_id, product.sku, product.name, params.date_from, params.date_to]);

  return { rows, error };
}

export default function ProductDocumentsSheet({ product, params, onClose }) {
  const { rows, error } = useProductDocuments(product, params);
  return (
    <FilterSheet open onClose={onClose} title={product.name}>
      <div>
        <div className="sheet-kv">
          <label>ספק</label>
          <span style={{ fontWeight: 600, textAlign: 'start' }}>{product.supplier_name}</span>
        </div>
        {error ? (
          <p style={{ margin: '12px 0 0', color: '#94a3b8', fontSize: 12.5 }}>
            שגיאה בטעינת המסמכים.
          </p>
        ) : rows == null ? (
          <div className="loading-container" style={{ minHeight: 80 }}><div className="spinner" /></div>
        ) : rows.length === 0 ? (
          <p style={{ margin: '12px 0 0', color: '#94a3b8', fontSize: 12.5 }}>
            לא נמצאו מסמכים.
          </p>
        ) : (
          // Wrapped for the same reason every nested table on this page is:
          // a 5-column table inside a 375px sheet needs its own horizontal
          // scroll, not to blow out the sheet's width.
          <div className="table-scroll-mobile" style={{ marginTop: 8 }}>
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>סוג מסמך</th>
                  <th>מספר</th>
                  <th>תאריך</th>
                  <th>כמות</th>
                  <th>סכום (ללא מע״מ)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(d => (
                  <tr key={d.doc_id}>
                    <td>{d.doc_type}</td>
                    <td>
                      {/* Opens the actual document — the whole point of
                          wiring this up: a count you could not act on now
                          leads straight to the invoices behind it. `.link` is
                          scoped to .breadcrumb elsewhere in this codebase, so
                          the affordance is inlined instead of borrowed. */}
                      <Link
                        to={`/document/${d.doc_id}`}
                        onClick={onClose}
                        style={{ color: '#0f3460', textDecoration: 'underline', textUnderlineOffset: 2 }}
                      >
                        {d.doc_num || `#${d.doc_id}`}
                      </Link>
                    </td>
                    <td>{formatDateShort(d.date)}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{d.qty}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(d.spend)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </FilterSheet>
  );
}

// A product's documents as sub-lines under its row, from 641px (#318). The
// document number is BLUE when the document has a scan and opens it in a new
// tab (owner, 23/10/2026: a linked number, not a "צפה במסמך" button) — a
// button styled as a link, since the scan is fetched with the user's login
// and cannot be an href; plain when there is none. Nothing here navigates
// away from the report.
export function ProductDocumentsPanel({ product, params }) {
  const { rows, error } = useProductDocuments(product, params);
  let body;
  if (error) body = <p className="pdocs-muted">שגיאה בטעינת המסמכים.</p>;
  else if (rows == null) body = <div className="loading-container" style={{ minHeight: 60 }}><div className="spinner" /></div>;
  else if (rows.length === 0) body = <p className="pdocs-muted">לא נמצאו מסמכים.</p>;
  else {
    body = (
      <table className="pdocs-table">
        <thead>
          <tr>
            <th className="pdocs-col-date">תאריך</th>
            <th>מסמך</th>
            <th className="pdocs-num pdocs-col-qty">כמות</th>
            <th className="pdocs-num pdocs-col-sum">סכום (ללא מע״מ)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(d => (
            <tr key={d.doc_id}>
              <td className="pdocs-num">{formatDateShort(d.date)}</td>
              <td>
                {d.file_path ? (
                  <button type="button" className="pdocs-doc-link" title="פתיחת המסמך בלשונית חדשה" onClick={() => openScanInNewTab(d.file_path)}>
                    {d.doc_num || `#${d.doc_id}`}
                  </button>
                ) : (d.doc_num || '—')}
                {' '}<span className="pdocs-muted pdocs-type">· {d.doc_type}</span>
              </td>
              <td className="pdocs-num">{d.qty}</td>
              <td className="pdocs-num">{formatCurrency(d.spend)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return <div className="pdocs-panel">{body}</div>;
}
